import {
  ConversationLifecycleEventV1,
  validateLifecycleEventV1,
} from '@optima/optima-contracts';

export const LIFECYCLE_EVENT_VERSION = 'conversation.turn.lifecycle.v1' as const;

export type ConversationTurnState =
  | 'RECEIVED'
  | 'QUEUED'
  | 'UNDERSTANDING'
  | 'STEERING'
  | 'COMPOSING'
  | 'VALIDATING'
  | 'WAITING_REVIEW'
  | 'PREPARING_REPLY'
  | 'SENDING'
  | 'SENT'
  | 'FAILED'
  | 'SUPERSEDED'
  | 'RECONCILED';

export interface ConversationLifecycleEvent {
  eventVersion: typeof LIFECYCLE_EVENT_VERSION;
  eventId: string;
  sessionId: string;
  workerId: string;
  conversationTurnId: string;
  sequence: number;
  state: ConversationTurnState;
  occurredAt: string;
  reasonCode: string | null;
  retryable: boolean;
}

export interface LifecycleReplayPage {
  events: ConversationLifecycleEvent[];
  nextCursor: number;
  synchronized: boolean;
  hasMore?: boolean;
  cursorExpired?: boolean;
}

const STATES = new Set<ConversationTurnState>([
  'RECEIVED', 'QUEUED', 'UNDERSTANDING', 'STEERING', 'COMPOSING', 'VALIDATING', 'WAITING_REVIEW',
  'PREPARING_REPLY', 'SENDING', 'SENT', 'FAILED', 'SUPERSEDED', 'RECONCILED',
]);

export const parseLifecycleEvent = (
  value: unknown,
  workerId = '',
  expectedSessionId?: string,
): ConversationLifecycleEvent | null => {
  if (!validateLifecycleEventV1(value).valid) return null;
  const event = value as ConversationLifecycleEventV1;
  if (expectedSessionId && event.sessionId !== expectedSessionId) return null;
  const state = event.payload.phase === 'WAITING_FOR_REVIEW'
    ? 'WAITING_REVIEW'
    : event.payload.phase;
  if (!STATES.has(state as ConversationTurnState)) return null;
  return {
    eventVersion: LIFECYCLE_EVENT_VERSION,
    eventId: event.eventId,
    sessionId: event.sessionId,
    workerId,
    conversationTurnId: event.payload.turnId,
    sequence: event.eventCursor,
    state: state as ConversationTurnState,
    occurredAt: event.occurredAt,
    reasonCode: event.payload.reasonCode ?? null,
    retryable: event.payload.retryable ?? false,
  };
};
